import { useAtom, useAtomValue } from "jotai";
import { ChevronRight, Menu } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/auth";
import { getGreeting, formatTodayLong } from "@/lib/greeting";
import { pageHeaderAtom } from "@/store/ui";
import { sidebarMobileOpenAtom } from "@/store/sidebar";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import { HeaderBrandLinks } from "./HeaderBrandLinks";
import { DocsLink } from "./DocsLink";

function getUserInitials(
  firstName: string | undefined,
  lastName: string | undefined,
): string {
  const first = firstName?.[0]?.toUpperCase() ?? "";
  const last = lastName?.[0]?.toUpperCase() ?? "";
  return first + last || "?";
}

interface Props {
  /**
   * IANA timezone for the long-form date (TASK-5043).
   *
   * A PROP, emphatically not a `useTenantTimezone()` call in here: this
   * component's test renders `<Header />` with a Jotai provider and a router
   * but NO `QueryClientProvider`, and the hook throws «No QueryClient set» —
   * verified, not assumed. `AppShell` owns the zone and passes it down. This is
   * the same conclusion TASK-5041 reached for `AnalyticsFilterBar`.
   *
   * Omitted → the browser zone, the previous behaviour.
   */
  tenantTz?: string;
}

/**
 * Top header bar — reads title and breadcrumbs from the pageHeaderAtom.
 * Each page sets the atom via usePageHeader hook.
 */
export function Header({ tenantTz }: Props) {
  const user = useAuthStore((s) => s.user);
  const { breadcrumbs } = useAtomValue(pageHeaderAtom);
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [, setMobileOpen] = useAtom(sidebarMobileOpenAtom);
  const initials = getUserInitials(user?.first_name, user?.last_name);

  const isDashboard = location.pathname === "/dashboard";
  const topbarText = isDashboard
    ? `${getGreeting(t)}, ${user?.first_name ?? ""}`
    : formatTodayLong(new Date(), i18n.language, tenantTz);

  function handleToggleLang() {
    const next = i18n.language === "en" ? "ru" : "en";
    void i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
  }

  const langLabel = i18n.language === "ru" ? "RU" : "EN";

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-100 bg-white px-4 shadow-[0_1px_0_0_rgb(0,0,0,0.04)] lg:px-6">
      {/* Left: hamburger (mobile only) + breadcrumbs + title */}
      <div className="flex items-center gap-3">
        {/* Hamburger button — visible only below lg breakpoint */}
        <button
          type="button"
          aria-label={t("nav.openMenu")}
          onClick={() => setMobileOpen(true)}
          className="flex items-center justify-center rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 lg:hidden"
        >
          <Menu size={20} />
        </button>

        <div className="flex flex-col justify-center">
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <nav
              aria-label="Breadcrumb"
              className="mb-0.5 flex items-center gap-1"
            >
              {breadcrumbs.map((crumb, index) => (
                <span key={index} className="flex items-center gap-1">
                  {index > 0 && (
                    <ChevronRight size={12} className="text-gray-300" />
                  )}
                  {crumb.to ? (
                    <Link
                      to={crumb.to}
                      className="text-xs font-medium text-gray-400 hover:text-gray-600"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-xs font-medium text-gray-400">
                      {crumb.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          ) : null}
          <p className="text-lg font-bold text-gray-900">{topbarText}</p>
        </div>
      </div>

      {/* Right: brand links + docs + language switcher + notification bell + user avatar */}
      <div className="flex items-center gap-3">
        {/* Brand header links — rendered only when the active brand supplies
            any (default/platform brand → empty array → nothing rendered). */}
        <HeaderBrandLinks />

        {/* Documentation launcher (TASK-4770) — mints the docs cookie, then
            opens /docs/ in a new tab. Shared with AdminShell and DealerShell so
            the mint-then-open behaviour cannot drift between them. */}
        <DocsLink />

        {/* Language switcher */}
        <button
          type="button"
          onClick={handleToggleLang}
          aria-label="Switch language"
          className="rounded-lg border border-gray-300 px-2 py-0.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
        >
          {langLabel}
        </button>

        {/* Notification bell with dropdown */}
        <NotificationBell />

        {/* User avatar: image when set, initials circle otherwise */}
        {user?.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={initials}
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-xs font-semibold text-white">
            {initials}
          </div>
        )}
      </div>
    </header>
  );
}
